import { type ComponentProps, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/util/utils';

type KeyValueProps = Omit<ComponentProps<'div'>, 'children'> &
    VariantProps<typeof keyValueVariants> & {
        label: ReactNode;
        value: ReactNode;
    };

/**
 * kv-pair, READ 100:1630: a label in text-xs muted over a value in text-sm, 4px apart. Twelve
 * of them in a line detail panel, six in the totals card. `row` puts label and value on one
 * line with the value at the far end, for money.
 */
function KeyValue({ label, value, align, tone, row, className, ...props }: KeyValueProps) {
    return (
        <div
            data-slot="key-value"
            className={cn(keyValueVariants({ align, tone, row }), className)}
            {...props}
        >
            <span data-slot="key-value-label" className="text-muted-foreground text-xs leading-4">
                {label}
            </span>
            <span
                data-slot="key-value-value"
                className="text-sm leading-5 [overflow-wrap:anywhere]"
            >
                {value}
            </span>
        </div>
    );
}

const keyValueVariants = cva('flex min-w-0 flex-col gap-1', {
    variants: {
        align: {
            start: '',
            end: 'items-end text-right',
        },
        tone: {
            default: '[&>[data-slot=key-value-value]]:text-foreground',
            muted: '[&>[data-slot=key-value-value]]:text-muted-foreground',
            locked: '[&>[data-slot=key-value-value]]:text-text-locked',
        },
        row: {
            true: 'flex-row items-baseline justify-between gap-4',
            false: '',
        },
    },
    defaultVariants: { align: 'start', tone: 'default', row: false },
});

export { KeyValue, type KeyValueProps };
